import axios, { AxiosResponse } from "axios";
import apiClient from "../../../../shared/infrastructure/api/client";
import {
    BoardState,
    ConstellationBoard,
    ConstellationScene,
    ConstellationScenesPage,
    ConstellationSessionsPage,
    JoinResponse,
    LaunchResponse,
    ListConstellationSessionsParams,
    PublicBoard,
    SessionType,
    ShareLink,
} from "../../domain/types";

// Public (patient) client: NO Firebase auth interceptor — the patient is
// unauthenticated and authorized solely by the URL token.
const publicClient = axios.create({
    baseURL: "/api",
    headers: { "Content-Type": "application/json" },
});

export const ConstellationAPI = {
    // --- Therapist (authenticated) ---------------------------------------
    launch: (
        sessionType: SessionType,
        patientId: string | null,
        couplePatientId?: string,
        title?: string,
        sceneId?: string,
        groupPatientId?: string,
    ): Promise<AxiosResponse<LaunchResponse>> =>
        apiClient.post("/constellation/sessions", {
            session_type: sessionType,
            patient_id: patientId ?? undefined,
            couple_patient_id: couplePatientId,
            group_patient_id: groupPatientId,
            title,
            scene_id: sceneId,
        }),

    // Therapist-wide list of their constellation sessions (with filters).
    listSessions: (
        params?: ListConstellationSessionsParams,
    ): Promise<AxiosResponse<ConstellationSessionsPage>> =>
        apiClient.get("/constellation/sessions", { params }),

    // Erase a constellation session (GDPR Art. 17): removes snapshots + cascades
    // the board, participants and share links.
    deleteSession: (sessionId: string): Promise<AxiosResponse<void>> =>
        apiClient.delete(`/constellation/sessions/${sessionId}`),

    // Attach a constellation board to an existing session (e.g. a recording).
    launchForSession: (sessionId: string): Promise<AxiosResponse<LaunchResponse>> =>
        apiClient.post(`/constellation/sessions/${sessionId}/board`),

    // Start a new session seeded from a board's current state (same subject).
    newSessionFromBoard: (boardId: string): Promise<AxiosResponse<LaunchResponse>> =>
        apiClient.post(`/constellation/boards/${boardId}/new-session`),

    // Whether a past session has a constellation board (for its detail page).
    getSessionBoardSummary: (
        sessionId: string,
    ): Promise<AxiosResponse<{ has_board: boolean; board_id: string | null; puppet_count: number }>> =>
        apiClient.get(`/constellation/sessions/${sessionId}/board-summary`),

    // Start a new session seeded from a past session's board state.
    newSessionFromSession: (sessionId: string): Promise<AxiosResponse<LaunchResponse>> =>
        apiClient.post(`/constellation/sessions/${sessionId}/new-from-board`),

    getBoard: (boardId: string): Promise<AxiosResponse<ConstellationBoard>> =>
        apiClient.get(`/constellation/boards/${boardId}`),

    updateBoardState: (
        boardId: string,
        state: BoardState,
        expectedVersion?: number,
    ): Promise<AxiosResponse<ConstellationBoard>> =>
        apiClient.put(`/constellation/boards/${boardId}/state`, {
            state,
            expected_version: expectedVersion,
        }),

    createShareLink: (boardId: string): Promise<AxiosResponse<ShareLink>> =>
        apiClient.post(`/constellation/boards/${boardId}/share-link`),

    revokeShareLink: (boardId: string): Promise<AxiosResponse<void>> =>
        apiClient.delete(`/constellation/boards/${boardId}/share-link`),

    uploadSnapshot: (boardId: string, blob: Blob): Promise<AxiosResponse<ConstellationBoard>> => {
        const form = new FormData();
        form.append("file", blob, "snapshot.png");
        return apiClient.post(`/constellation/boards/${boardId}/snapshots`, form, {
            headers: { "Content-Type": "multipart/form-data" },
        });
    },

    renameSnapshot: (
        boardId: string,
        index: number,
        name: string,
    ): Promise<AxiosResponse<ConstellationBoard>> =>
        apiClient.patch(`/constellation/boards/${boardId}/snapshots/${index}`, { name }),

    deleteSnapshot: (
        boardId: string,
        index: number,
    ): Promise<AxiosResponse<ConstellationBoard>> =>
        apiClient.delete(`/constellation/boards/${boardId}/snapshots/${index}`),

    reassignFigureOwner: (
        boardId: string,
        figureId: string,
        ownerId: string | null,
    ): Promise<AxiosResponse<ConstellationBoard>> =>
        apiClient.patch(`/constellation/boards/${boardId}/figures/${figureId}/owner`, {
            owner_id: ownerId,
        }),

    close: (
        boardId: string,
        notes: string,
        saveScene?: { name: string; description?: string },
    ): Promise<AxiosResponse<{ status: string }>> =>
        apiClient.post(`/constellation/boards/${boardId}/close`, {
            notes,
            save_scene: saveScene,
        }),

    // --- Scenes (therapist-only) -----------------------------------------
    listScenes: (params?: {
        patient_id?: string;
        is_template?: boolean;
    }): Promise<AxiosResponse<ConstellationScenesPage>> =>
        apiClient.get("/constellation/scenes", { params }),

    getScene: (sceneId: string): Promise<AxiosResponse<ConstellationScene>> =>
        apiClient.get(`/constellation/scenes/${sceneId}`),

    createScene: (payload: {
        name: string;
        description?: string;
        board_id?: string;
        state?: BoardState;
        patient_id?: string;
        couple_patient_id?: string;
        is_template?: boolean;
        /** Save to the caller's centre library instead of their own. */
        share_with_center?: boolean;
    }): Promise<AxiosResponse<ConstellationScene>> =>
        apiClient.post("/constellation/scenes", payload),

    updateScene: (
        sceneId: string,
        payload: { name?: string; description?: string; state?: BoardState },
    ): Promise<AxiosResponse<ConstellationScene>> =>
        apiClient.patch(`/constellation/scenes/${sceneId}`, payload),

    deleteScene: (sceneId: string): Promise<AxiosResponse<void>> =>
        apiClient.delete(`/constellation/scenes/${sceneId}`),

    loadScene: (
        boardId: string,
        sceneId: string,
        ownerMapping?: Record<string, string | null>,
    ): Promise<AxiosResponse<ConstellationBoard>> =>
        apiClient.post(`/constellation/boards/${boardId}/load-scene`, {
            scene_id: sceneId,
            owner_mapping: ownerMapping,
        }),

    getSessionSnapshotCount: (
        sessionId: string,
    ): Promise<AxiosResponse<{ count: number; names?: string[] }>> =>
        apiClient.get(`/constellation/sessions/${sessionId}/snapshots`),

    getSessionSnapshot: (
        sessionId: string,
        index: number,
    ): Promise<AxiosResponse<Blob>> =>
        apiClient.get(`/constellation/sessions/${sessionId}/snapshots/${index}`, {
            responseType: "blob",
        }),

    kickParticipant: (boardId: string, participantId: string): Promise<AxiosResponse<void>> =>
        apiClient.delete(`/constellation/boards/${boardId}/participants/${participantId}`),

    // --- Patient (public, token-scoped) ----------------------------------
    getPublicBoard: (token: string, participantId?: string): Promise<AxiosResponse<PublicBoard>> =>
        publicClient.get(`/public/constellation/${token}`, {
            params: participantId ? { participant_id: participantId } : undefined,
        }),

    joinBoard: (token: string, displayName: string): Promise<AxiosResponse<JoinResponse>> =>
        publicClient.post(`/public/constellation/${token}/join`, { display_name: displayName }),

    updatePublicState: (
        token: string,
        state: BoardState,
        expectedVersion?: number,
        participantId?: string,
    ): Promise<AxiosResponse<PublicBoard>> =>
        publicClient.put(`/public/constellation/${token}/state`, {
            state,
            expected_version: expectedVersion,
            participant_id: participantId,
        }),
};
